/**
 * web/src/components/gates/EmailThreadViewer.tsx
 * Collapsed Gmail-style conversation history for the ops_next_steps gate, so the
 * reviewer sees the latest context (welcome email + the sponsor's replies)
 * before composing the Ops Next Steps email.
 *
 * Data comes from GET /api/workflows/:id/ops-email-thread — a LIVE read of the
 * thread from the mailbox that SENT the welcome (it holds the whole thread),
 * falling back to the reviewer's own send-from mailbox. The server never errors
 * for the unavailable cases; it returns `available: false` with a `reason`,
 * which we render as a friendly one-liner rather than an error.
 *
 * All rows are collapsed by default; the reviewer expands the one(s) they want
 * to read. Each message's HTML body is sanitized (sanitizeEmailHtml) before
 * render.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { sanitizeEmailHtml } from '../../lib/sanitize';
import { Skeleton } from '../shared/Skeleton';
import type { OpsEmailThread, OpsEmailThreadMessage } from '../../api/types';

/** Friendly empty-state copy per server `reason` (available === false). */
const REASON_COPY: Record<string, string> = {
  no_welcome: 'The welcome email hasn’t been sent yet — there’s no conversation to show.',
  not_connected: 'Connect your Gmail to see the conversation here.',
  message_not_found:
    'Couldn’t find the original welcome conversation. The welcome sender may have disconnected their Gmail — you can still send.',
  read_failed: 'Couldn’t load the conversation right now. You can still send — try refreshing.',
};

function _fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'MMM d, h:mm a');
}

function _senderLabel(m: OpsEmailThreadMessage): string {
  return m.fromName?.trim() || m.fromEmail || 'Unknown sender';
}

function ThreadRow({ message, defaultOpen }: { message: OpsEmailThreadMessage; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const safeHtml = message.bodyHtml ? sanitizeEmailHtml(message.bodyHtml) : null;
  return (
    <div className="border-t border-border first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start gap-2 px-3.5 py-2.5 text-left hover:bg-bg-subtle/60 transition-colors"
      >
        <Icon
          name="chevron-right"
          size={13}
          className={cn('text-text-faint mt-0.5 shrink-0 transition-transform', open && 'rotate-90')}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'text-[13px] font-semibold truncate',
                message.fromMailbox ? 'text-text-muted' : 'text-text',
              )}
              title={message.fromEmail}
            >
              {_senderLabel(message)}
            </span>
            {message.fromMailbox && (
              <span className="text-[10px] uppercase tracking-[0.06em] text-text-faint shrink-0">
                you
              </span>
            )}
            <span className="ml-auto text-[11px] text-text-subtle tabular shrink-0">
              {_fmtDate(message.date)}
            </span>
          </div>
          {!open && (
            <div className="text-[12px] text-text-subtle truncate mt-0.5">{message.snippet}</div>
          )}
        </div>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pl-[30px]">
          {safeHtml ? (
            <div
              className="text-[13px] text-text leading-[1.5] [&_a]:text-accent [&_a]:underline break-words"
              // Sanitized via DOMPurify (sanitizeEmailHtml) immediately above.
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          ) : (
            <pre className="text-[13px] text-text leading-[1.5] whitespace-pre-wrap font-sans break-words">
              {message.bodyText || '(no content)'}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export interface EmailThreadViewerProps {
  thread: OpsEmailThread | undefined;
  isLoading: boolean;
  className?: string;
}

/**
 * Renders the collapsed conversation history above the ops compose box.
 * Shows a skeleton while loading, a friendly note when the thread is
 * unavailable, or the message list (all collapsed) when present.
 *
 * @sideEffects none (pure render; data fetched by the caller's hook)
 * @idempotent yes
 */
export function EmailThreadViewer({ thread, isLoading, className }: EmailThreadViewerProps) {
  if (isLoading) {
    return <Skeleton variant="row" height={64} className={className} />;
  }
  if (!thread) return null;

  const wrap = (children: React.ReactNode) => (
    <section className={cn('rounded-[10px] border border-border bg-bg overflow-hidden', className)}>
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border bg-bg-subtle/50">
        <Icon name="mail" size={13} className="text-text-faint" />
        <span className="text-[11px] uppercase tracking-[0.07em] font-semibold text-text-subtle">
          Conversation so far
        </span>
        {thread.available && (
          <span className="ml-auto text-[11px] text-text-subtle">
            {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}
            {thread.mailbox ? ` · ${thread.mailbox}` : ''}
          </span>
        )}
      </div>
      {children}
    </section>
  );

  if (!thread.available) {
    const copy = (thread.reason && REASON_COPY[thread.reason]) || REASON_COPY.read_failed;
    return wrap(<p className="px-3.5 py-3 text-[12.5px] text-text-muted leading-[1.5]">{copy}</p>);
  }

  // All messages collapsed by default — the reviewer expands the one(s) they
  // want to read. (Server returns thread order, oldest first.)
  return wrap(
    <div>
      {thread.messages.map((m) => (
        <ThreadRow key={m.id} message={m} defaultOpen={false} />
      ))}
    </div>,
  );
}
